/**
 * The predicate every layer asks of a field, with the stable code a refusal of it carries. A rule is written here once;
 * the canonical validators, the pattern expander and the skill-language checker call it and map the code at their own
 * edge, so one field value is accepted or refused alike wherever it is first met.
 */
export interface FieldRule {
  readonly code: string;
  readonly holds: (value: unknown) => boolean;
}

const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const finitePositive = (value: unknown): boolean => finite(value) && value > 0;
const integerAtLeast = (minimum: number) => (value: unknown): boolean => typeof value === 'number' && Number.isInteger(value) && value >= minimum;

/** The fields of a spatial formation (a pattern's distribution of members). */
export const FORMATION_FIELD_RULES = {
  /** A formation of several members: a one-member formation is a single. */
  memberCount: { code: 'COMPOSITE_FORMATION_COUNT_INVALID', holds: integerAtLeast(2) },
  angleDeg: { code: 'COMPOSITE_FORMATION_ANGLE_INVALID', holds: finite },
  spacingM: { code: 'COMPOSITE_FORMATION_SPACING_INVALID', holds: finitePositive },
  radiusM: { code: 'COMPOSITE_FORMATION_RADIUS_INVALID', holds: finitePositive },
} as const satisfies Record<string, FieldRule>;

/**
 * The extent of a skill-owned area. A zero extent is restricted as an authoring rule: a point is spelt POINT, and an
 * area that reaches nothing is a mis-authored one.
 */
export const GEOMETRY_FIELD_RULES = {
  areaRadiusM: { code: 'BAD_RADIUS', holds: finitePositive },
  sectorReachM: { code: 'BAD_SECTOR', holds: finitePositive },
  sectorAngleDeg: { code: 'BAD_SECTOR', holds: (value: unknown): boolean => finitePositive(value) && (value as number) <= 360 },
} as const satisfies Record<string, FieldRule>;
